import { ReactNode, useEffect, useState } from "react";

// Literals, not a template string — Tailwind only emits classes it can see.
const TOOLTIP_POSITION = {
  top: "tooltip-top",
  bottom: "tooltip-bottom",
  left: "tooltip-left",
  right: "tooltip-right",
} as const;

function CopyButton({
  text,
  children,
  tooltip,
  tooltipPosition = "bottom",
  className = "mr-2 text-sm",
  ariaLabel,
}: {
  text: string;
  /** Replaces the default text label — a preformatted value, or an icon. */
  children?: ReactNode;
  /** Resting tooltip, default "Copy". Pass the value when it is not shown inline. */
  tooltip?: string;
  /** Required for icon-only children; a tooltip is invisible to screen readers. */
  ariaLabel?: string;
  /** Never "bottom" inside a daisyUI `.collapse` — overflow:hidden clips it. */
  tooltipPosition?: keyof typeof TOOLTIP_POSITION;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
  };

  // Timer lives in the effect so it is cleared on unmount.
  useEffect(() => {
    if (!copied) return;

    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      onClick={copyToClipboard}
      aria-label={ariaLabel}
      className={`tooltip ${TOOLTIP_POSITION[tooltipPosition]} ${className}`}
      data-tip={copied ? "Copied!" : (tooltip ?? "Copy")}
    >
      {children ?? (
        /* Conditionally apply underline based on text value */
        <span
          className={
            text === "1.866.568.8326"
              ? "text-[12px] font-semibold underline"
              : "ml-1 text-nowrap text-[12px]"
          }
        >
          {text}
        </span>
      )}
    </button>
  );
}

export default CopyButton;
